import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { CATEGORIES, calcsInCategory } from '../data/calculators'
import { useRegion } from '../lib/region'
import type { Region } from '../lib/region'
import { useLocale, localizedHref, toEnPath, FR_ENABLED } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcShort, calcTagline, categoryName } from '../i18n/calculators'

function useTheme() {
  const [dark, setDark] = useState(false)
  useEffect(() => {
    const saved = localStorage.getItem('rs-theme')
    const prefers = window.matchMedia('(prefers-color-scheme: dark)').matches
    const d = saved ? saved === 'dark' : prefers
    setDark(d)
    document.documentElement.classList.toggle('dark', d)
  }, [])
  const toggle = () =>
    setDark((p) => {
      const n = !p
      document.documentElement.classList.toggle('dark', n)
      localStorage.setItem('rs-theme', n ? 'dark' : 'light')
      return n
    })
  return [dark, toggle] as const
}

export default function Header() {
  const [dark, toggleTheme] = useTheme()
  const [region, setRegion] = useRegion()
  const [toolsOpen, setToolsOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  // Hover bridge: when the mouse leaves the trigger button on its way down
  // to the mega-menu, defer the close by ~120ms so a re-enter on the menu
  // cancels it. Same handler is shared by the menu itself, so leaving the
  // menu also closes after a brief grace period.
  const closeTimer = useRef<number | null>(null)
  const openTools = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
    setToolsOpen(true)
  }
  const closeTools = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setToolsOpen(false), 120)
  }

  const locale = useLocale()
  const { pathname } = useLocation()
  const L = (en: string) => localizedHref(en, locale) // locale-aware href
  const tr = (k: string) => t(locale, k)

  // Close on route change (clicking a link inside the dropdown).
  useEffect(() => { setToolsOpen(false) }, [pathname])
  // Close on Escape.
  useEffect(() => {
    if (!toolsOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setToolsOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [toolsOpen])

  // Language switcher targets: EN = English version of this page; FR = French
  // version if it exists, otherwise the French home.
  const curEn = toEnPath(pathname)
  const enTarget = curEn
  const frTarget = FR_ENABLED.has(curEn) ? localizedHref(curEn, 'fr') : '/fr/'

  const navLink = 'rounded-md px-2.5 py-2 text-sm font-medium text-text-body hover:bg-surface-2 whitespace-nowrap'
  const navLinkActive = 'rounded-md px-2.5 py-2 text-sm font-medium text-brand-text hover:bg-surface-2 whitespace-nowrap'

  const ToolsButton = () => (
    <button
      className={navLink + ' flex items-center gap-1'}
      onClick={() => (toolsOpen ? setToolsOpen(false) : openTools())}
      onMouseEnter={openTools}
      onMouseLeave={closeTools}
      aria-expanded={toolsOpen}
      aria-haspopup="true"
    >
      {tr('nav.tools')} <span className="text-xs opacity-60">▾</span>
    </button>
  )

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-surface/95 backdrop-blur">
      <div className="mx-auto flex h-20 max-w-6xl items-center gap-3 px-4 md:h-[140px]">
        <Link to={L('/')} className="flex items-center mr-1" onClick={() => setMobileOpen(false)}>
          {/* Logo image contains the wordmark + tagline, so no separate text needed.
              width/height attributes match the source PNG (1200×630) so the browser
              reserves correct space → eliminates CLS. fetchPriority + the
              <link rel="preload"> in index.html mark it as the LCP element. */}
          <img
            src="/header-logo.webp"
            alt="RenoSheets"
            width={500}
            height={263}
            fetchPriority="high"
            decoding="async"
            className="block h-auto w-[160px] sm:w-[200px] md:w-[250px]"
          />
        </Link>

        {/* Full nav (xl) */}
        <nav className="ml-1 hidden items-center gap-0.5 xl:flex">
          <Link to={L('/about/')} className={navLink}>{tr('nav.about')}</Link>
          <Link to={L('/how-to-use/')} className={navLink}>{tr('nav.howToUse')}</Link>
          <Link to={L('/kanban-board/')} className={navLinkActive}>{tr('nav.planner')}</Link>
          <ToolsButton />
          <Link to={L('/my-sheets/')} className={navLink}>{tr('nav.mySheets')}</Link>
          <Link to={L('/resources/')} className={navLink}>{tr('nav.resources')}</Link>
        </nav>

        {/* Condensed nav (md–xl) */}
        <nav className="ml-1 hidden items-center gap-0 md:flex xl:hidden">
          <Link to={L('/about/')} className={navLink}>{tr('nav.about')}</Link>
          <Link to={L('/how-to-use/')} className={navLink}>{tr('nav.howToUse')}</Link>
          <Link to={L('/kanban-board/')} className={navLinkActive}>{tr('nav.planner')}</Link>
          <ToolsButton />
          <Link to={L('/my-sheets/')} className={navLink}>{tr('nav.mySheets')}</Link>
          <Link to={L('/resources/')} className={navLink}>{tr('nav.resources')}</Link>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {/* Language switcher */}
          <div className="flex items-center overflow-hidden rounded-md border border-border text-xs font-medium" aria-label={tr('a11y.language')}>
            <Link
              to={enTarget}
              onClick={() => setMobileOpen(false)}
              className={locale === 'en' ? 'bg-brand-text px-2 py-1 text-on-primary' : 'px-2 py-1 text-text-muted hover:bg-surface-2'}
            >
              EN
            </Link>
            <Link
              to={frTarget}
              onClick={() => setMobileOpen(false)}
              className={locale === 'fr' ? 'bg-brand-text px-2 py-1 text-on-primary' : 'px-2 py-1 text-text-muted hover:bg-surface-2'}
            >
              FR
            </Link>
          </div>

          <select
            aria-label={tr('a11y.region')}
            value={region}
            onChange={(e) => setRegion(e.target.value as Region)}
            className="rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-body"
          >
            <option value="us">🇺🇸 US</option>
            <option value="ca">🇨🇦 CA</option>
          </select>
          <button
            onClick={toggleTheme}
            aria-label={tr('a11y.theme')}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-text-body hover:bg-surface-2"
          >
            {dark ? '☀' : '☾'}
          </button>
          <button
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={tr('a11y.menu')}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-text-body md:hidden"
          >
            ☰
          </button>
        </div>
      </div>

      {/* Full-width mega-menu — fixed below the sticky header. 5 columns, one
          per category. Hidden on mobile (the mobile menu below handles that). */}
      {toolsOpen && (
        <div
          className="fixed inset-x-0 top-20 z-40 hidden border-b border-border bg-surface shadow-xl md:top-[140px] md:block"
          onMouseEnter={openTools}
          onMouseLeave={closeTools}
        >
          <div className="mx-auto max-w-7xl px-6 py-8">
            <div className="grid grid-cols-5 gap-x-6 gap-y-2">
              {CATEGORIES.map((cat) => (
                <div key={cat.slug}>
                  <Link
                    to={L(`/calculators/${cat.slug}/`)}
                    className="block border-b border-border pb-2 text-xs font-semibold uppercase tracking-wide text-brand-text hover:underline"
                    onClick={() => setToolsOpen(false)}
                  >
                    {categoryName(cat.slug, cat.name, locale)}
                  </Link>
                  <ul className="mt-3 space-y-1">
                    {calcsInCategory(cat.slug).map((c) => (
                      <li key={c.slug}>
                        <Link
                          to={L(`/calculators/${c.slug}/`)}
                          className="-mx-2 flex items-start gap-2.5 rounded-md p-2 hover:bg-surface-2"
                          onClick={() => setToolsOpen(false)}
                        >
                          <span className="mt-0.5 text-lg leading-none text-brand-text" aria-hidden>{c.icon}</span>
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="text-sm font-medium text-text">{calcShort(c.slug, c.short, locale)}</span>
                            <span className="text-xs text-text-muted">{calcTagline(c.slug, c.tagline, locale)}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-border bg-surface px-4 py-3 md:hidden">
          <div className="grid grid-cols-2 gap-1 mb-3">
            <Link to={L('/about/')} className="block rounded px-2 py-2 text-sm font-medium text-text-body hover:bg-surface-2" onClick={() => setMobileOpen(false)}>{tr('nav.about')}</Link>
            <Link to={L('/how-to-use/')} className="block rounded px-2 py-2 text-sm font-medium text-text-body hover:bg-surface-2" onClick={() => setMobileOpen(false)}>{tr('nav.howToUse')}</Link>
            <Link to={L('/kanban-board/')} className="block rounded px-2 py-2 text-sm font-medium text-brand-text hover:bg-surface-2" onClick={() => setMobileOpen(false)}>{tr('nav.planner')}</Link>
            <Link to={L('/my-sheets/')} className="block rounded px-2 py-2 text-sm font-medium text-text-body hover:bg-surface-2" onClick={() => setMobileOpen(false)}>{tr('nav.mySheets')}</Link>
            <Link to={L('/resources/')} className="block rounded px-2 py-2 text-sm font-medium text-text-body hover:bg-surface-2" onClick={() => setMobileOpen(false)}>{tr('nav.resources')}</Link>
          </div>
          <div className="mb-2 border-t border-border pt-2 text-xs font-semibold uppercase tracking-wide text-brand-text">{tr('nav.tools')}</div>
          <ul className="grid grid-cols-2 gap-1">
            {CATEGORIES.flatMap((cat) => calcsInCategory(cat.slug)).map((c) => (
              <li key={c.slug}>
                <Link to={L(`/calculators/${c.slug}/`)} className="block rounded px-2 py-1.5 text-sm text-text-body hover:bg-surface-2" onClick={() => setMobileOpen(false)}>
                  {calcShort(c.slug, c.short, locale)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  )
}
